import React, {Component} from 'react';
import {FieldsController, getNearMarkedParent} from "../../lib/fields_controller";
import extend_element from "../../lib/xpath_extend/element";
import {ContextMenu, MenuItemConfig} from "./contextMenu";
import {
    ActionType,
    extendBlockAction,
    extendElement,
    markAction,
    MenuAction,
    setMetaField,
    unextendBlock,
    unmarkAction
} from "./contextActions";
import block_extend from "../../lib/xpath_extend/block";
import {Field, Fields} from "../../lib/fields";
import {IsCrawlSchema, ObjectSchema, ParserConfig} from "../../api/config";

import "./visualXPathEditor.css";

const structuredClone = require('@ungap/structured-clone').default;


function buildMetaActions(schema: ObjectSchema): Array<MenuItemConfig> {
    const ret = new Array<MenuItemConfig>();

    for (const fieldName in schema.fields) {
        const field = schema.fields[fieldName];
        if (field.type == "url" || fieldName == "id") {
            ret.push({
                title: `Установить для "${field.title}" текущий URL`,
                context: setMetaField(fieldName, "/pressindex/current_parse_url/@value"),
                enabled: true,
                childs: [],
            })
        }
    }

    if (ret.length > 0) {
        return [{
            title: "Meta",
            context: null,
            enabled: true,
            childs: ret,
        }]
    }

    return []
}

function buildMenuForElem(schema: ObjectSchema, elem: HTMLElement, elemFields: Set<string>, nearExtendableElem: HTMLElement | null, isExtended: boolean, canUnextend: boolean): Array<MenuItemConfig> {
    const menuForField = (field: string, title: string): Array<MenuItemConfig> => {
        return [
            {
                title: title,
                context: markAction(field),
                enabled: !elemFields.has(field),
                childs: []
            },
            {
                title: `Снять: ${title}`,
                context: unmarkAction(field),
                enabled: elemFields.has(field),
                childs: []
            }
        ];
    }

    const menuForFields = [];
    for (const fieldName in schema.fields) {
        const field = schema.fields[fieldName];
        menuForFields.push(...menuForField(fieldName, field.title),)
    }

    return [
        ...menuForFields,
        ...buildMetaActions(schema),
        {
            title: "Обобщить элемент",
            context: extendElement(),
            enabled: elemFields.size > 0 && extend_element.canExtendElementXPath(elem) && !isExtended,
            childs: [],
        },
        {
            title: "Обобщить блок",
            context: extendBlockAction(),
            enabled: nearExtendableElem != null && !isExtended,
            childs: [],
        },
        {
            title: "Разбить блок",
            context: unextendBlock(),
            enabled: canUnextend,
            childs: [],
        },
    ]
}


function getModelFromConfig(document: Document, config: ParserConfig | null, schema: ObjectSchema): Fields {
    const fields = new Fields(document, config?.item_block_xpath || null);
    for (const fieldName in schema.fields) {
        const fieldSchema = schema.fields[fieldName];

        let xpath: Array<string> = [];
        if (config?.fields) {
            const fieldConfig = config.fields[fieldName];

            if (fieldConfig) {
                xpath = fieldConfig.xpath || [];
            }
        }

        fields.fields.set(fieldName, new Field(fields, fieldName, fieldSchema.type, new Set(xpath)))
    }

    return fields
}

function getConfigByModel(config: ParserConfig, fields: Fields): ParserConfig {
    const resp: ParserConfig = {...config, fields: {}}

    for (const field of fields.fields.values()) {
        const origField = config.fields[field.name];

        resp.fields[field.name] = {
            ...origField,
            type: field.type,
            xpath: Array.from(field.xpath_list)
        }
    }

    resp.item_block_xpath = fields.blockXPath;
    return resp
}


interface XPatherState {
    controller: FieldsController | null,
    currentConfig: ParserConfig
    menu: Array<MenuItemConfig>

    contextMenuSelectedElem: HTMLElement | null
    nearExtendableElem: HTMLElement | null
    contextMenuVisible: boolean
    contextMenuLeft: number
    contextMenuTop: number
}

interface XPatherProps {
    config: ParserConfig | null
    schema: ObjectSchema
    onConfigChanged?: (model: any, reason: string) => void
    proxy: string
    url: string,
    loading: boolean,
    onLoaded?: () => void
}

class VisualXPathEditor extends Component<XPatherProps, XPatherState> {
    iframeRef: React.RefObject<HTMLIFrameElement>

    constructor(props: any) {
        super(props);
        this.iframeRef = React.createRef();
        this.state = {
            controller: null,
            currentConfig: structuredClone(this.props.config),
            nearExtendableElem: null,
            contextMenuSelectedElem: null,
            contextMenuVisible: false,
            contextMenuLeft: 0,
            contextMenuTop: 0,
            menu: []
        }
    }

    onIframeClick(target: HTMLElement, x: number, y: number) {
        if (this.state.contextMenuVisible) {
            this.closePopup();
            return
        }

        const isCrawlSchema = IsCrawlSchema(this.props.schema)
        const elem = getNearMarkedParent(target as HTMLElement, isCrawlSchema);

        elem.classList.add("pi-focused");
        const editor = this.state.controller!;
        const nearExtendableElem = isCrawlSchema ? null : block_extend.getNearExtendableSegment(editor.fields, elem);
        const nearBlock = editor.getNearBlock(elem);

        this.setState({
            nearExtendableElem: nearExtendableElem,
            contextMenuSelectedElem: elem,
            contextMenuVisible: true,
            contextMenuLeft: x,
            contextMenuTop: y,
            menu: buildMenuForElem(this.props.schema!, elem, editor.getFieldsForElem(elem), nearExtendableElem, editor.fields.blockXPath != null, nearBlock != null),
        });
    }

    closePopup() {
        this.state.contextMenuSelectedElem!.classList.remove("pi-focused");
        this.setState({
            contextMenuVisible: false,
        });
    }

    onMenuClick(context: MenuAction) {
        console.log("menu clicked", context, this.state.contextMenuSelectedElem);
        switch (context.action) {
            case ActionType.MarkElement:
                this.state.controller!.selectElem(context.mark_params!.fieldName, this.state.contextMenuSelectedElem!);
                break

            case ActionType.UnmarkElement:
                this.state.controller!.unselectElem(context.mark_params!.fieldName, this.state.contextMenuSelectedElem!);
                break

            case ActionType.ExtendBlock:
                const rootElem = this.state.nearExtendableElem!;
                this.state.controller!.extendBlock(rootElem);
                break

            case ActionType.UnextendBlock:
                const unextendBlock = this.state.controller!.getNearBlock(this.state.contextMenuSelectedElem!)!;
                this.state.controller!.unextendBlock(unextendBlock);
                break

            case ActionType.ExtendElement:
                this.state.controller!.extendElement(this.state.contextMenuSelectedElem!);
                break

            case ActionType.SetMetaValueToField:
                const field = this.state.controller!.fields.fields.get(context.set_meta_value_params!.fieldName)!;
                field.xpath_list = new Set([context.set_meta_value_params!.xpath]);
                field.updateMarks();
        }

        const newConfig = getConfigByModel(this.state.currentConfig, this.state.controller!.fields);
        this.setState({
            currentConfig: newConfig,
        })
        this.props.onConfigChanged && this.props.onConfigChanged({...newConfig}, "menu");

        this.closePopup();
    }

    componentWillReceiveProps(nextProps: XPatherProps) {
        if (this.state.controller) {
            const model = getModelFromConfig(this.state.controller.document, nextProps.config, nextProps.schema)
            let config = nextProps.config
            if (this.props.schema != nextProps.schema) {
                config = getConfigByModel(nextProps.config || {
                    fields: {},
                    item_block_xpath: ""
                }, model)
            }
            this.setState({
                currentConfig: config!,
            })

            this.state.controller!.setModel(model);

            if (this.props.schema != nextProps.schema) {
                // Если сменилась схема, то отправляем инфу наверх о том, что конфиг значительно поменялся
                this.props.onConfigChanged && this.props.onConfigChanged(config, "schema changed");
            }
        }
    }

    onIFrameLoaded() {
        const iframe = this.iframeRef.current!;
        const document = iframe.contentDocument!;

        prepareIframeDocument(document);
        if (this.state.controller) {
            this.state.controller.document.onclick = null;
        }

        document.onclick = (ev) => {
            const iframe = this.iframeRef.current!;
            ev.preventDefault();
            this.onIframeClick(
                ev.target as HTMLElement,
                iframe.offsetLeft + ev.clientX,
                iframe.offsetTop + ev.clientY
            );
        };

        const model = getModelFromConfig(document, this.props.config, this.props.schema)
        const config = getConfigByModel(this.state.currentConfig || {
            fields: {},
            item_block_xpath: ""
        }, model)

        const controller = new FieldsController(document);
        controller.setModel(model);
        this.setState({
            controller: controller,
        }, () => {
            this.props.onConfigChanged && this.props.onConfigChanged(config, "iframe loaded")
            this.props.onLoaded && this.props.onLoaded()
        });
    }

    render() {
        return (
            <>
                {
                    this.iframeRef.current && <ContextMenu
                        x={this.state.contextMenuLeft}
                        y={this.state.contextMenuTop}
                        visible={this.state.contextMenuVisible}
                        menu={this.state.menu}
                        onClick={(context) => this.onMenuClick(context)}
                    />
                }

                {
                    this.props.loading && <div className={"overlay"} style={{
                        width: this.iframeRef.current?.offsetWidth,
                        height: this.iframeRef.current?.offsetHeight,
                        left: this.iframeRef.current?.offsetLeft,
                        top: this.iframeRef.current?.offsetTop,
                    }}>
                    </div>
                }

                {this.props.url && this.props.proxy &&
                    <iframe
                        ref={this.iframeRef}
                        className={this.props.loading ? "loading" : ""}
                        style={{height: "75vh", width: "100%"}}
                        src={`/api/html?proxy=${this.props.proxy}&url=${encodeURIComponent(this.props.url)}`}
                        onLoad={() => this.onIFrameLoaded()}
                        sandbox={"allow-same-origin"}
                    />
                }
            </>
        )
    }
}

export default VisualXPathEditor;


function prepareIframeDocument(target: Document) {
    async function injectCSS() {
        const resp = await fetch("/xpather_inject.css")
        const cssLink = target.createElement("style")
        cssLink.textContent = await resp.text();
        target.head.appendChild(cssLink);
    }

    injectCSS().catch(console.error);

    // disable forms and submit buttons
    for (let elem of target.getElementsByTagName("form")) {
        elem.onsubmit = (e: Event) => {
            e.preventDefault();
        }
    }
}
